import { uniqueId } from "es-toolkit/compat";
import { useMemo } from "react";

import { FormFieldContext } from "./FormFieldContext";
import type { FormValues, TestAttribute } from "./shared";

// Shamelessly stolen from https://dev.to/pffigueiredo/typescript-utility-keyof-nested-object-2pa3
// For a nested object: { foo: { bar: 1; } }, valid keys are "foo" and "foo.bar".
type NestedKeyOf<ObjectType extends object> = {
  [Key in keyof ObjectType & (string | number)]: ObjectType[Key] extends object
    ? `${Key}` | `${Key}.${NestedKeyOf<ObjectType[Key]>}`
    : `${Key}`;
}[keyof ObjectType & (string | number)];

export type FormFieldProps<T extends FormValues> = TestAttribute & {
  name: Extract<NestedKeyOf<T>, string>;
  children: React.ReactNode;
  explicitLabel?: boolean;
  /** Disables the whole field: the control(s) inherit it and the label greys out. */
  disabled?: boolean;
};

export const FormField = <T extends FormValues>({
  name,
  explicitLabel,
  disabled,
  dataTest,
  children,
}: FormFieldProps<T>) => {
  const controlId = useMemo(() => uniqueId(name), [name]);

  return (
    <FormFieldContext.Provider value={{ name, controlId, explicitLabel, disabled, dataTest }}>
      {children}
    </FormFieldContext.Provider>
  );
};
